import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Mutex } from 'async-mutex';
import { isFileError } from '../../utilities/is-file-error';
import { LZStringService } from '../l-z-string/l-z-string.service';
import { BaseStorageService } from './base-storage.service';
import { FileDexieService } from './file-dexie.service';

@Injectable()
export class IndexedDbStorageService extends BaseStorageService {
    public constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        protected readonly LZString: LZStringService,
        protected readonly dexieFileStorage: FileDexieService,
    ) {
        super(log, LZString);
    }

    protected async checkFile(mutex: Mutex, fileName: string): Promise<boolean> {
        const unlock = await mutex.acquire();
        return this.dexieFileStorage
            .getFileByName(fileName)
            .then(({ file }) => {
                return !!file;
            })
            .catch((error) => {
                // if the file was not cached, ignore the NOT_FOUND_ERR (=> 1)
                const suppressError = isFileError(error) && error.code === 1;
                if (suppressError) {
                    return false;
                }

                throw error;
            })
            .finally(() => unlock());
    }

    protected async readAsArrayBuffer(
        mutex: Mutex,
        fileName: string,
    ): Promise<{ buffer: ArrayBuffer; timestamp: number }> {
        const unlock = await mutex.acquire();
        return this.dexieFileStorage
            .getFileByName(fileName)
            .then(({ file, timestamp }) => {
                return file.arrayBuffer().then((buffer) => ({ buffer, timestamp }));
            })
            .finally(() => {
                unlock();
            });
    }

    protected async removeFile(mutex: Mutex, fileName: string): Promise<void> {
        const unlock = await mutex.acquire();
        this.log.debug(`IndexedDbStorageService: removeFile: locking '${fileName}' for saving data`);
        return this.dexieFileStorage
            .removeFileEntry(fileName)
            .then(() => {})
            .finally(() => {
                unlock();
            });
    }

    protected async writeFile(
        mutex: Mutex,
        fileName: string,
        content: Uint8Array,
        timestamp: number = Date.now(),
    ): Promise<void> {
        const unlock = await mutex.acquire();
        return this.dexieFileStorage
            .writeFileEntry(fileName, new Blob([content]), timestamp)
            .then(() => {})
            .finally(() => {
                unlock();
            });
    }
}
